import { HTTPClient } from '../HTTPClient';
import { AuthenticationClientConfig } from './AuthenticationClientConfig';
import { AuthenticationClientRedirecter } from './AuthenticationClientRedirecter';
import { PKCEChallengeProvider } from './PKCEChallengeProvider';

interface ILoginWithCodeFlowParams {
    redirect_uri: string;
    response_type?: 'code';
    connection?: 'facebook' | 'google-oauth2' | 'apple';
    emulateAuthenticatedUser?: boolean;
    forceInteractiveLogin?: boolean;
    createUser?: boolean;
    isTestUser?: boolean;
}

export class AuthenticationClient {
    static inject = () => [AuthenticationClientRedirecter, AuthenticationClientConfig, PKCEChallengeProvider];
    constructor(
        private redirecter: AuthenticationClientRedirecter,
        private config: AuthenticationClientConfig,
        private pkceChallengeProvider: PKCEChallengeProvider
    ){}

    loginWithCodeFlow = async ({ redirect_uri, response_type = 'code', connection, emulateAuthenticatedUser = false, forceInteractiveLogin = false, createUser = false, isTestUser = false }: ILoginWithCodeFlowParams): Promise<boolean> => {

        const { code_challenge } = this.pkceChallengeProvider.createChallenge();

        const params = new URLSearchParams();
        params.set('redirect_uri', redirect_uri);
        params.set('response_type', response_type);
        params.set('code_challenge', code_challenge);

        params.set('useNormalRedirect', 'true');

        if (connection) {
            params.set('connection', connection);
        }
        if (emulateAuthenticatedUser){
            params.set('emulateAuthenticatedUser', 'true');
        }
        if (isTestUser){
            params.set('isTestUser', 'true');
        }
        if (forceInteractiveLogin){
            params.set('forceInteractiveLogin', 'true');
        }

        if (createUser){
            params.set('createUser', 'true');
        }

        const apiUrlForAuthFlow = `${this.config.baseURL}/oauth/authorize?${params.toString()}`;

        return await this.redirecter.redirect(apiUrlForAuthFlow);
    }

    exchangeCode = async ({ code, currentUserAccessToken }: { code: string, currentUserAccessToken?: string }) => {
        const { code_verifier } = this.pkceChallengeProvider.getLastVerifier();
        if (!code_verifier){
            throw new Error('code_verifier is required to exchange code using PKCE flow');
        }
        const headers: any = {};
        if (currentUserAccessToken){
            headers.authorization = `JWT ${currentUserAccessToken}`;
        }
        const body: any = {
            grant_type: 'code',
            code,
            code_verifier
        };
        const res = await new HTTPClient({ baseURL: this.config.baseURL }).post(`/oauth/token`, body, headers);

        return res.data;
    }

    getGuestAccessToken = async ({ isTestUser = false}: { isTestUser?: boolean} = {}) => {
        const headers: any = {};
        const body: any = {
            // client_credentials is the "flow"
            // we're not sending any credentials b/c this "No authentication (public client)"
            grant_type: 'client_credentials',
            createUser: true,
            isTestUser
        };
        const res = await new HTTPClient({ baseURL: this.config.baseURL }).post(`/oauth/token`, body, headers);

        return res.data;
    }

    refreshAccessToken = async ({ refresh_token }: { refresh_token: string }) => {
        const res = await new HTTPClient({ baseURL: this.config.baseURL }).post(`/oauth/token`, {
            grant_type: 'refresh',
            refresh_token
        });

        return res.data;
    }

    revokeRefreshToken = async ({ refresh_token }: { refresh_token: string }): Promise<void> => {
        await new HTTPClient({ baseURL: this.config.baseURL }).post(`/oauth/revoke`, { refresh_token });
    }

    logout = async ({ redirect_uri }: { redirect_uri: string }) => {
        const url = `${this.config.baseURL}/auth/logout?returnTo=${encodeURIComponent(redirect_uri)}`;

        await this.redirecter.redirect(url);
    }

    exchangeOldAccessToken = async ({ access_token }: { access_token: string}) => {
        const res = await new HTTPClient({ baseURL: this.config.baseURL }).post(`/oauth/token`, {
            grant_type: 'old-access-token',
            access_token
        });

        return res.data;
    }
}
